import type { JSONSchema } from 'json-schema-typed';
import { Fetcher } from '../api/impl/utils/Fetcher';
import { KeyValuePairs } from '../api/KeyValueStoreAPI';
import type { APIMetainfo, SharedStateDelayedAction } from '../api/ReplicantAPI';
import { type ABTestDynamicConfig, isPlaceboTestId } from '../common/ReplicantABTests';
import type { ABTestUserListConfig, default as DB, Entry, ScheduledAction } from '../db/DB';
import { ReplicantError } from '../Errors';
import { IndexDB } from '../indexdb/IndexDB';
import logger from '../logger';
import { adminProfilePropRoles } from '../ReplicantAdminProfileConfig';
import type { ChatbotMessageInstance, Renderers } from '../ReplicantChatbot';
import { createComputedPropertiesAPI, resolveComputedProperties } from '../ReplicantComputedProperties';
import { Replicant } from '../ReplicantConfig';
import type { QueryFilters } from '../ReplicantIndexes';
import { MessageSignature } from '../ReplicantMessages';
import { Ruleset } from '../ReplicantRuleset';
import { SharedStateCounterMap, sharedStateHasSearchableProperties, SharedStates } from '../ReplicantSharedStates';
import SB from '../SchemaBuilder';
import { applyChangesetsToSnapshot } from '../server/StateRecovery';
import { chunk, sortBy } from '../utils/ArrayUtils';
import { computedPropertiesToQuerySchema } from '../utils/computedPropertiesToQuerySchema';
import { generateMessageId } from '../utils/MessageUtils';
import { deepCopy, deepMerge } from '../utils/ObjUtils';
import { schemaBuilderToJSONSchema } from '../utils/schemaBuilderToJSONSchema';
import { unreachable } from '../utils/Utils';
import { APIError } from './APIError';
import type { NarrowcastAPIClient } from './NarrowcastAPIClient';
import type { APIAccessInterface, PostMessagesFn } from './ServerReplicant';
import ServerReplicantAdminTasks from './ServerReplicantAdminTasks';
import ServerReplicantChatbot, { SendChatbotMessageResult } from './ServerReplicantChatbot';
import {
    GLOBAL_SYSTEM_SCHEDULED_ACTION_USER_ID,
    SYSTEM_SCHEDULED_ACTION_NAMES,
    SystemScheduledActions,
} from './ServerReplicantSystemScheduledActions';
import { TaskRunnerClient, taskSchemas } from './TaskRunner';
import { sendSlackNotification } from './utils/slack';

/** The user ID used as message sender for user and shared state messags posted via the admin API. */
const ADMIN_MESSAGE_SENDER_ID = 'admin';

const LINE_CHATBOT_BROADCAST_NOTIFICATION_ID_PREFIX = 'lineChatbotBroadcast-';

export const adminAPISchema = {
    fetchProfile: SB.object({ id: SB.string() }),
    fetchUserAtRevision: SB.object({ id: SB.string(), rev: SB.int() }),
    postAdminMessage: SB.object({
        id: SB.string(),
        message: SB.object({
            name: SB.string(),
            args: SB.unknown(),
        }),
    }),
    postAdminMessageToMultiple: SB.object({
        ids: SB.array(SB.string()),
        message: SB.object({
            name: SB.string(),
            args: SB.unknown(),
        }),
    }),
    getAdminMessages: SB.object({}),
    fetchChangesets: SB.object({
        id: SB.string(),
        num: SB.number().optional(),
        page: SB.number().optional(),
        date: SB.string().optional(),
    }),

    // Tasks:
    listTasks: SB.object({}),
    runTask: SB.union([
        SB.object({
            task: SB.tuple<'chatbotBroadcast'>(['chatbotBroadcast']), // TS compiler stops inferring generic type due to nesting depth so we declare type manually
            args: taskSchemas.chatbotBroadcast,
        }),
        SB.object({
            task: SB.tuple<'reindex'>(['reindex']),
            args: taskSchemas.reindex,
        }),
        SB.object({
            task: SB.tuple<'reindexIndexed'>(['reindexIndexed']),
            args: taskSchemas.reindexIndexed,
        }),
        SB.object({
            task: SB.tuple<'scheduleActionToMany'>(['scheduleActionToMany']),
            args: taskSchemas.scheduleActionToMany,
        }),
    ]),
    stopTask: SB.object({ id: SB.string() }),
    restartTask: SB.object({ id: SB.string() }),

    // Index queries:
    countPlayers: SB.object({ where: SB.map(SB.unknown()) }), // TODO type more reliably. Should accept 'where' clause of searchPlayers/countPlayers
    fetchFromIndexDB: SB.object({ id: SB.string() }),

    fetchESQuerySchema: SB.object({}),
    runESQuery: SB.object({
        where: SB.map(SB.unknown()), // TODO type more reliably. Should accept 'where' clause of searchPlayers/countPlayers
        limit: SB.number().optional(),
    }),

    // Chatbot messages:
    getChatbotMessageTemplates: SB.object({}),
    sendChatbotMessage: SB.object({
        receiverId: SB.string(),
        payload: SB.map(SB.unknown()),
        analyticsUserProperties: SB.map(SB.unknown()),

        /** Chatbot message template defined in `config.adminTools.chatbotMessageTemplates` */
        template: SB.object({ name: SB.string(), args: SB.unknown() }),

        /** Force a message delivery channel, or leave undefined to use the default fallback strategy */
        forceChannel: SB.tuple(['ios', 'chatbot']).optional(), // TODO add 'android' when Android push notifications PR is merged
    }),

    // AB tests
    fetchABTests: SB.object({}),
    updateABTests: SB.map(
        SB.object({
            active: SB.boolean().optional(),
            rollOut: SB.number().range(0, 1).optional(),
            newUsersRollOut: SB.union([SB.number().range(0, 1).optional(), SB.null()]) as SB.Optional<
                SB.Schema<number | null | undefined>
            >,
            stopAssignment: SB.boolean().optional(),
        }),
    ),

    fetchUserLists: SB.object({}),
    updateUserLists: SB.array(
        SB.union([
            SB.object({
                type: SB.tuple<'add'>(['add']),
                userList: SB.object({
                    id: SB.string(),
                    name: SB.string(),
                    testIds: SB.array(SB.string()).optional(),
                    userIds: SB.array(SB.string()),
                    bucketIds: SB.array(SB.string()),
                }),
                before: SB.string().optional(),
            }).customValidator((value) =>
                value.userList.id === value.before ? `Cannot add user list ${value.userList.id} before itself` : null,
            ),

            SB.object({
                type: SB.tuple<'remove'>(['remove']),
                userListId: SB.string(),
            }),

            SB.object({
                type: SB.tuple<'move'>(['move']),
                userListId: SB.string(),
                before: SB.string().optional(),
            }).customValidator((value) =>
                value.userListId === value.before ? `Cannot move user list ${value.userListId} before itself` : null,
            ),

            SB.object({
                type: SB.tuple<'update'>(['update']),
                userList: SB.object({
                    id: SB.string(),
                    name: SB.string().optional(),
                    testIds: SB.union([SB.null(), SB.array(SB.string())]).optional(),
                    userIds: SB.array(SB.string()).optional(),
                    bucketIds: SB.array(SB.string()).optional(),
                }),
            }),
        ]),
    ),

    // General purpose
    deleteUsers: SB.array(SB.string()),
    deleteUserData: SB.object({
        userIds: SB.array(SB.string()),
        sources: SB.object({
            amplitude: SB.boolean(),
            sentry: SB.boolean(),
            replicant: SB.boolean(),
            indexDB: SB.boolean(),
        }).optional(),
    }),
    fetchConfig: SB.object({}),

    // LINE chatbot broadcast
    getScheduledLineChatbotBroadcasts: SB.object({}),
    scheduleLineChatbotBroadcasts: SB.array(
        SB.object({ id: SB.string(), opts: SB.map(SB.unknown()), sendAt: SB.int() }),
    ),
    unscheduleLineChatbotBroadcasts: SB.array(SB.string()),

    // Shared states:
    fetchSharedState: SB.object({ stateId: SB.string(), stateName: SB.string() }),
    fetchSharedStateIndexSchemas: SB.object({}),
    fetchSharedStateMessages: SB.object({}),
    postSharedStateMessage: SB.object({
        stateIds: SB.array(SB.string()),
        stateName: SB.string(),
        message: SB.object({
            name: SB.string(),
            args: SB.unknown(),
        }),
    }),
    searchSharedStates: SB.object({
        where: SB.map(SB.unknown()),
        stateName: SB.string(),
        limit: SB.number().optional(),
    }),
    countSharedStates: SB.object({
        where: SB.map(SB.unknown()),
        stateName: SB.string(),
    }),

    // KV store:
    fetchKVStore: SB.object({ keys: SB.array(SB.string()), internal: SB.boolean() }),
    updateKVStore: SB.object({ pairs: SB.map(SB.string()), internal: SB.boolean() }),
};

export type FetchABTestsOutput = {
    [key: string]: {
        buckets: readonly { id: string }[];
        assignManually: boolean | undefined;
        active: boolean;
        rollOut: number;
        newUsersRollOut?: number;
        stopAssignment?: boolean;
    };
};

type AdminAction = keyof typeof adminAPISchema;

const validateAdminAction = (action: string) => {
    if (!(action in adminAPISchema)) {
        throw new ReplicantError('Unknown action: ' + action, 'replication_error', 'admin_interface_error');
    }

    return action as AdminAction;
};

const validateAdminArgs = <TAction extends AdminAction>(action: TAction, args: unknown) => {
    try {
        const argsWithDefaults = adminAPISchema[action].tryValidateAndFillDefaults(args);
        if (argsWithDefaults !== null) {
            args = argsWithDefaults;
        }
    } catch (e: any) {
        throw new ReplicantError(
            'Could not validate args for action ' + action + ': ' + e.message,
            'replication_error',
            'admin_interface_error',
        );
    }

    return args as SB.ExtractType<(typeof adminAPISchema)[TAction]>;
};

export default class ServerReplicantAdmin<T extends Replicant> {
    private adminTasks?: ServerReplicantAdminTasks;

    constructor(
        private opts: {
            apiAccess: APIAccessInterface<T>;
            chatbot: ServerReplicantChatbot<T>;
            db: DB<T['state'], T['messages']>;
            deleteUserData: (opts: {
                userId: string;
                sources?: {
                    amplitude: boolean;
                    sentry: boolean;
                    replicant: boolean;
                    indexDB: boolean;
                };
            }) => Promise<void>;
            fetcher: Fetcher;
            indexDB?: IndexDB<any>;
            narrowcastAPIClient: NarrowcastAPIClient;
            postMessages: PostMessagesFn;
            taskRunnerClient?: TaskRunnerClient;
        },
    ) {
        if (opts.taskRunnerClient) {
            this.adminTasks = new ServerReplicantAdminTasks({
                config: opts.apiAccess.config,
                dateNow: opts.apiAccess.dateNow,
                db: opts.db,
                taskRunnerClient: opts.taskRunnerClient,
            });
        }
    }

    async handleAdminAction(params: { action: string; args: unknown }) {
        const action = validateAdminAction(params.action);

        switch (action) {
            case 'fetchProfile': {
                const args = validateAdminArgs(action, params.args);
                return this.handleAdminFetchProfile(args.id);
            }
            case 'fetchUserAtRevision': {
                const args = validateAdminArgs(action, params.args);
                return this.handleAdminFetchUserAtRevision(args);
            }
            case 'postAdminMessage': {
                const args = validateAdminArgs(action, params.args);
                return this.handleAdminPostAdminMessage(args.id, args.message);
            }
            case 'postAdminMessageToMultiple': {
                const args = validateAdminArgs(action, params.args);
                return this.handleAdminPostAdminMessageToMultiple(args.ids, args.message);
            }
            case 'getAdminMessages': {
                return this.handleAdminGetAdminMessages();
            }
            case 'fetchChangesets': {
                const args = validateAdminArgs(action, params.args);
                return this.handleAdminFetchChangesets(args.id, args.num, args.page, args.date);
            }
            case 'listTasks': {
                if (!this.adminTasks) {
                    throw new APIError('Tasks not configured');
                }

                return this.adminTasks.handleListTasks();
            }
            case 'runTask': {
                if (!this.adminTasks) {
                    throw new APIError('Tasks not configured');
                }

                const args = validateAdminArgs(action, params.args);
                return this.adminTasks.handleRunTask(args);
            }
            case 'stopTask': {
                if (!this.adminTasks) {
                    throw new APIError('Tasks not configured');
                }

                const args = validateAdminArgs(action, params.args);
                return this.adminTasks.handleStopTask(args);
            }
            case 'restartTask': {
                if (!this.adminTasks) {
                    throw new APIError('Tasks not configured');
                }

                const args = validateAdminArgs(action, params.args);
                return this.adminTasks.handleRestartTask(args);
            }
            case 'countPlayers': {
                const args = validateAdminArgs(action, params.args);
                return this.handleCountPlayers(args.where);
            }

            case 'fetchFromIndexDB': {
                const args = validateAdminArgs(action, params.args);
                return this.handleAdminFetchFromIndexDB(args.id);
            }

            case 'fetchESQuerySchema': {
                return this.handleFetchESQuerySchema();
            }
            case 'runESQuery': {
                const args = validateAdminArgs(action, params.args);
                return this.handleRunESQuery(args.where, args.limit);
            }
            case 'getChatbotMessageTemplates': {
                return this.handleGetChatbotMessageTemplates();
            }
            case 'sendChatbotMessage': {
                const args = validateAdminArgs(action, params.args);
                return this.handleSendChatbotMessage(args);
            }

            case 'fetchABTests': {
                return this.handleFetchABTests();
            }
            case 'updateABTests': {
                const args = validateAdminArgs(action, params.args);
                return this.handleUpdateABTests(args);
            }
            case 'fetchUserLists': {
                return this.handleFetchUserLists();
            }
            case 'updateUserLists': {
                const args = validateAdminArgs(action, params.args);
                return this.handleUpdateUserLists(args);
            }

            case 'deleteUsers': {
                const args = validateAdminArgs(action, params.args);
                return this.handleDeleteUsers(args);
            }
            case 'deleteUserData': {
                const args = validateAdminArgs(action, params.args);
                return this.handleDeleteUserData(args);
            }
            case 'fetchConfig': {
                return this.opts.apiAccess.config;
            }

            case 'getScheduledLineChatbotBroadcasts': {
                return this.handleGetScheduledLineChatbotBroadcasts();
            }
            case 'scheduleLineChatbotBroadcasts': {
                const args = validateAdminArgs(action, params.args);
                return this.handleScheduleLineChatbotBroadcasts(args);
            }
            case 'unscheduleLineChatbotBroadcasts': {
                const args = validateAdminArgs(action, params.args);
                return this.handleUnscheduleLineChatbotBroadcasts(args);
            }

            case 'fetchSharedState': {
                const args = validateAdminArgs(action, params.args);
                return this.handleFetchSharedState(args);
            }

            case 'fetchSharedStateIndexSchemas': {
                return this.handleFetchSharedStateIndexSchemas();
            }

            case 'fetchSharedStateMessages': {
                return this.handleFetchSharedStateMessages();
            }

            case 'postSharedStateMessage': {
                const args = validateAdminArgs(action, params.args);

                return this.handlePostSharedStateMessage(args);
            }

            case 'searchSharedStates': {
                const args = validateAdminArgs(action, params.args);
                return this.handleSearchSharedStates(args);
            }

            case 'countSharedStates': {
                const args = validateAdminArgs(action, params.args);
                return this.handleCountSharedStates(args);
            }

            case 'fetchKVStore': {
                const args = validateAdminArgs(action, params.args);
                return this.handleFetchKVStore(args);
            }

            case 'updateKVStore': {
                const args = validateAdminArgs(action, params.args);
                return this.handleUpdateKVStore(args);
            }

            default: {
                return unreachable(action);
            }
        }
    }

    private async handleAdminFetchProfile(id: string) {
        const entry = (await this.opts.apiAccess.getReadOnlyEntry(id))?.entry;
        if (!entry) {
            throw new APIError('Profile for ' + id + ' not found!', {
                statusCode: 404,
                statusDescription: 'Not Found',
            });
        }

        // Include the user properties defined in `config.adminTools.profile`:
        const profile: { caption: string; value: any; type: string; role?: string }[] = [];

        if (this.opts.apiAccess.config.adminTool?.profile?.length) {
            const api = createComputedPropertiesAPI({ entry, now: this.opts.apiAccess.dateNow, userId: id });
            const indexDoc = resolveComputedProperties({
                api,
                computedProperties: this.opts.apiAccess.config.computedProperties,
                docId: id,
                state: entry.state,
            });

            for (const { caption, property, role } of this.opts.apiAccess.config.adminTool.profile) {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                const computedProperty = this.opts.apiAccess.config.computedProperties![property];

                if (role) {
                    adminProfilePropRoles[role].tryValidate(indexDoc[property]);
                }

                profile.push({
                    caption,
                    type: computedProperty.type._type,
                    value: indexDoc[property],
                    role,
                });
            }
        }

        return {
            entry,
            messages: [], // TODO unused property: first remove references in https://github.com/play-co/replicant-cs-tool/ and then here
            profile,
        };
    }

    private async handleAdminFetchUserAtRevision(opts: { id: string; rev: number }): Promise<{
        metainfo: APIMetainfo;
        state: unknown;
        version: number;
    }> {
        const snapshots = await this.opts.db.snapshots.fetchSnapshots(opts.id);

        let nearestSnapshot: Entry<any> | undefined;
        for (const snapshot of snapshots.reverse()) {
            if (snapshot.rev <= opts.rev) {
                nearestSnapshot = snapshot;
                break;
            }
        }

        if (!nearestSnapshot) {
            throw new APIError('No snapshot found', { statusCode: 404, statusDescription: 'Not Found' });
        }

        const changesets =
            opts.rev > nearestSnapshot.rev
                ? await this.opts.db.fetchChangesetsBetween(opts.id, nearestSnapshot.rev + 1, opts.rev)
                : [];

        if (changesets.length !== opts.rev - nearestSnapshot.rev) {
            throw new APIError('Missing changesets between snapshot and target revision', {
                statusCode: 404,
                statusDescription: 'Not Found',
            });
        }

        const { metainfo, state, version } = applyChangesetsToSnapshot({
            changesets,
            migrator: this.opts.apiAccess.config.migrator,
            snapshot: nearestSnapshot,
        });

        return { metainfo, state, version };
    }

    private async handleAdminPostAdminMessage(id: string, adminMsg: { name: string; args: unknown }) {
        const userExists = !!(await this.opts.db.fetch(id)).entry;

        if (!userExists) {
            throw new APIError('Could not find user with id: ' + id);
        }

        await this.handleAdminPostAdminMessageToMultiple([id], adminMsg);
    }

    private async handleAdminPostAdminMessageToMultiple(ids: string[], adminMsg: { name: string; args: unknown }) {
        this.validateAdminMessage(adminMsg);

        const now = this.opts.apiAccess.dateNow();
        const msgId = generateMessageId(now, 0);

        const message = {
            id: msgId,
            name: adminMsg.name,
            args: adminMsg.args,
            sender: ADMIN_MESSAGE_SENDER_ID,
            senderClockOffset: 0,
        };

        const results: {
            [key: string]: {
                success: boolean;
                reason?: string;
            };
        } = {};

        // Post messages in concurrent batches of 100:
        const chunks = chunk(ids, 100);

        for (const chunk of chunks) {
            const chunkPromises = chunk.map(async (id) => {
                try {
                    await this.opts.postMessages({ receiverId: id, messages: [message] });

                    results[id] = {
                        success: true,
                    };
                } catch (e: any) {
                    results[id] = {
                        success: false,
                        reason: e.message ? e.message : e,
                    };
                }
            });

            await Promise.all(chunkPromises);
        }

        return results;
    }

    private validateAdminMessage(adminMsg: { name: string; args: unknown }) {
        const config = this.opts.apiAccess.config;
        if (!config.messages) {
            throw new APIError('No messages configured for this app');
        }

        const name = adminMsg.name;

        if (!config.messages[name]) {
            throw new APIError('Could not find message with name: ' + name);
        }

        const msg = config.messages[name];

        if (!msg.isAdmin) {
            throw new APIError('Cannot post message that is not admin.');
        }

        // Validate message arguments.
        const validationError = msg.schema.validate(adminMsg.args);
        if (validationError) {
            throw new APIError('Message arguments could not be validated: ' + validationError);
        }
    }

    private async handleAdminGetAdminMessages() {
        const config = this.opts.apiAccess.config;
        if (!config.messages) {
            throw new APIError('No messages configured for this app');
        }

        const messages: { name: string; args: object; description: string }[] = [];

        for (const name in config.messages) {
            // eslint-disable-next-line no-prototype-builtins
            if (!config.messages.hasOwnProperty(name)) continue;

            const msg: MessageSignature<T['state']> = config.messages[name];

            if (msg.isAdmin) {
                messages.push({
                    name,
                    args: msg.schema.serialize(),
                    description: msg.description,
                });
            }
        }

        const messageGroups = this.opts.apiAccess.config.adminTool?.adminMessageGroups ?? [];

        return { messages, messageGroups };
    }

    private async handleAdminFetchChangesets(id: string, num?: number, page?: number, date?: string) {
        let items;
        let totalItemsCount;
        let currentPage;

        if (date) {
            const {
                items: dateItems,
                totalItemsCount: dateTotalItemsCount,
                currentPage: dateCurrentPage,
            } = await this.opts.db.fetchChangesetsPageByDate(id, date, num);

            items = dateItems;
            totalItemsCount = dateTotalItemsCount;
            currentPage = dateCurrentPage;
        } else {
            const { items: pageItems, totalItemsCount: pageTotalItemsCount } = await this.opts.db.fetchChangesetsPage(
                id,
                page,
                num,
            );

            items = pageItems;
            totalItemsCount = pageTotalItemsCount;
            currentPage = page;
        }

        if (!items || items.length === 0) {
            throw new APIError('Changesets for profile id `' + id + '` not found!', {
                statusCode: 404,
                statusDescription: 'Not Found',
            });
        }

        const returnItems = items.map((item) => {
            delete item.diff;
            return item;
        });

        return { items: returnItems, totalItemsCount, currentPage };
    }

    private async handleCountPlayers(where: any) {
        const playersNum = await this.opts.apiAccess.countPlayers({ where }); // TODO add more validation

        return { playersNum };
    }

    private async handleAdminFetchFromIndexDB(id: string) {
        if (!this.opts.indexDB) {
            throw new APIError('Index DB is not available on replicant.', {
                statusCode: 418,
                statusDescription: 'Not available',
            });
        }

        return this.opts.indexDB.getMany([id]);
    }

    private async handleFetchESQuerySchema() {
        const config = this.opts.apiAccess.config;

        if (!config.computedProperties) {
            throw new APIError('No computed properties configured for this app');
        }

        const esQuerySchema = computedPropertiesToQuerySchema(config.computedProperties);

        return { esQuerySchema };
    }

    private async handleRunESQuery(where: any, limit = 100) {
        try {
            return await this.opts.apiAccess.searchPlayers({ where, limit }); // TODO add more validation
        } catch (e: any) {
            throw new APIError('Search players failed: ' + e.message);
        }
    }

    private handleGetChatbotMessageTemplates(): {
        name: string;
        argsSchema: JSONSchema;
        renderers: (keyof Renderers<any, any>)[];
    }[] {
        const { assets, ...templates } = this.opts.apiAccess.config.adminTool?.chatbotMessageTemplates ?? {};

        return Object.entries(templates).map(([name, templateFn]) => {
            const message = templateFn({ args: templateFn.argsSchema.getDefault(), payload: {} as any }); // Generate a dummy message instance to access the renderers property

            return {
                name,
                argsSchema: schemaBuilderToJSONSchema(templateFn.argsSchema),
                renderers: Object.keys(message.renderers) as (keyof Renderers<any, any>)[],
            };
        });
    }

    private async handleSendChatbotMessage(
        opts: SB.ExtractType<(typeof adminAPISchema)['sendChatbotMessage']>,
    ): Promise<SendChatbotMessageResult> {
        const template = this.opts.apiAccess.config.adminTool?.chatbotMessageTemplates?.[opts.template.name];
        if (!template) {
            throw Error(`Template '${opts.template.name}' not found in config.adminTool.chatbotMessageTemplates`);
        }

        const message: ChatbotMessageInstance = template({
            args: opts.template.args,
            payload: {
                $channel: 'CHATBOT',
                feature: 'cstool_admin_test_chatbot',
                $subFeature: null,
                ...opts.payload,
            },
            analyticsUserProperties: opts.analyticsUserProperties,
        });

        try {
            const status = await this.opts.chatbot.sendChatbotMessage({
                senderId: ADMIN_MESSAGE_SENDER_ID,
                receiverId: opts.receiverId,
                message,
                forceChannel: opts.forceChannel,
            });

            return status;
        } catch (error: any) {
            return { ok: false, reason: error.message, channels: {} };
        }
    }

    private async handleFetchABTests() {
        const ruleset: Ruleset['abTests'] = this.opts.apiAccess.config.ruleset?.abTests;
        if (!ruleset) {
            return {};
        }

        const config = await this.opts.db.abTests.get();

        const result: FetchABTestsOutput = {};

        for (const testId in ruleset) {
            if (isPlaceboTestId(testId)) {
                continue;
            }

            const { buckets, assignManually } = ruleset[testId]!;
            const { active, rollOut, newUsersRollOut, stopAssignment } = config[testId] ?? {
                active: false,
                rollOut: 0,
            };

            result[testId] = { buckets, assignManually, active, rollOut, newUsersRollOut, stopAssignment };
        }

        return result;
    }

    private async handleUpdateABTests(tests: SB.ExtractType<(typeof adminAPISchema)['updateABTests']>) {
        const ruleset: Ruleset['abTests'] = this.opts.apiAccess.config.ruleset?.abTests;
        if (!ruleset) {
            throw new APIError('AB Tests not configured. Cannot update.');
        }

        for (const testId in tests) {
            if (isPlaceboTestId(testId)) {
                throw new APIError(`Cannot update placebo test ${testId}`);
            }

            if (!ruleset[testId]) {
                throw new APIError(`Unexpected test id '${testId}'.`);
            }
        }

        const beforeConfig = await this.opts.db.abTests.get();

        for (const testId in tests) {
            const test = tests[testId]!;

            // Do not unset newUsersRollOut if the value is not modified:
            if (test.newUsersRollOut === undefined) {
                delete test.newUsersRollOut;
            }

            // Handle unsetting newUsersRollOut:
            if (test.newUsersRollOut === null) {
                test.newUsersRollOut = undefined;
            }

            if (!beforeConfig[testId]) {
                beforeConfig[testId] = { active: false, rollOut: 0 };
            }
        }

        const afterConfig = deepMerge(deepCopy(beforeConfig), tests);
        await this.opts.db.abTests.set(afterConfig);

        // Notify Slack channel about A/B test changes
        if (process.env.SLACK_OAUTH_TOKEN && process.env.SLACK_CHANNEL_ID) {
            const mentionUserIds =
                process.env.SLACK_STAKEHOLDER_USER_IDS?.split(',').map((userId) => userId.trim()) ?? [];
            if (mentionUserIds.length === 0) {
                // Default to always notifying somebody
                mentionUserIds.push(
                    'U01G4FCDBPU', // Mohak
                    'U066JQDR1H7', // Jaime
                );
            }

            const mentions = mentionUserIds.map((userId) => `<@${userId}>`).join(', ');
            const lines = [`Changes were made to A/B tests: (cc ${mentions})`, ''];

            // Write the fields of a test, optionally prefixing changed fields with an indicator
            const writeTestFields = (info: ABTestDynamicConfig, changed?: Partial<ABTestDynamicConfig>) => {
                const prefix = (changedField: unknown) => (changedField !== undefined ? ':point_right: ' : '');
                lines.push(`• ${prefix(changed?.active)}active: ${info.active ? '☑' : '◻'}`);
                lines.push(`• ${prefix(changed?.rollOut)}roll-out: ${Math.ceil(100 * info.rollOut)}%`);
                lines.push(
                    `• ${prefix(changed?.newUsersRollOut)}new users roll-out: ${Math.ceil(
                        100 * (info.newUsersRollOut ?? 0),
                    )}%`,
                );
                lines.push(`• ${prefix(changed?.stopAssignment)}stop assignment: ${info.stopAssignment ? '☑' : '◻'}`);
            };

            for (const testId in tests) {
                lines.push(`\`${testId}\` before:`);
                writeTestFields(beforeConfig[testId]!);
                lines.push('');
                lines.push(`\`${testId}\` after:`);
                writeTestFields(afterConfig[testId]!, tests[testId]);
                lines.push('');
            }

            await sendSlackNotification({
                fetcher: this.opts.fetcher,
                authToken: process.env.SLACK_OAUTH_TOKEN,
                channelId: process.env.SLACK_CHANNEL_ID,
                text: lines.join('\n'),
            });
        }
    }

    private handleFetchUserLists() {
        return this.opts.db.abTests.userLists.get();
    }

    private async handleUpdateUserLists(changes: SB.ExtractType<(typeof adminAPISchema)['updateUserLists']>) {
        const userLists = await this.opts.db.abTests.userLists.get();

        const add = (userList: ABTestUserListConfig, before: string | undefined) => {
            if (userLists.find(({ id }) => id === userList.id)) {
                throw new APIError(`updateUserLists: Cannot add '${userList.id}' which already exists.`);
            }

            if (before) {
                const index = userLists.findIndex(({ id }) => id === before);
                if (index < 0) {
                    throw new APIError(
                        `updateUserLists: Cannot add '${userList.id}' before '${before}' which doesn't exist.`,
                    );
                }

                userLists.splice(index, 0, userList);
            } else {
                userLists.push(userList);
            }
        };

        const remove = (userListId: string): ABTestUserListConfig => {
            const index = userLists.findIndex(({ id }) => id === userListId);
            if (index < 0) {
                throw new APIError(`updateUserLists: Cannot remove '${userListId}' which doesn't exist.`);
            }

            const [removed] = userLists.splice(index, 1);
            if (!removed) {
                // Sanity check.
                throw new Error(`updateUserLists: Unexpected lack of removed user list '${userListId}'.`);
            }

            return removed;
        };

        //

        for (const change of changes) {
            switch (change.type) {
                case 'add': {
                    add(change.userList, change.before);
                    break;
                }

                case 'remove': {
                    remove(change.userListId);
                    break;
                }

                case 'move': {
                    add(remove(change.userListId), change.before);
                    break;
                }

                case 'update': {
                    const index = userLists.findIndex(({ id }) => id === change.userList.id);
                    if (index < 0) {
                        throw new APIError(
                            `updateUserLists: Cannot update '${change.userList.id}' which doesn't exist.`,
                        );
                    }

                    userLists[index] = deepMerge(userLists[index]!, change.userList);

                    // Handle unsetting `testIds`:
                    if (change.userList.testIds === null) {
                        delete userLists[index]!.testIds;
                    }

                    break;
                }

                default:
                    unreachable(change);
            }
        }

        await this.opts.db.abTests.userLists.set(userLists);
    }

    /**
     * Prior to CORE-964, this endpoint handled deleting a list of userIds from the Replicant database only.
     * Once Replicant CS Tool is updated to use handleDeleteUserData endpoint, this endpoint will be removed.
     */
    private async handleDeleteUsers(userIds: SB.ExtractType<(typeof adminAPISchema)['deleteUsers']>) {
        return this.handleDeleteUserData({
            userIds,
            sources: { amplitude: false, sentry: false, replicant: true, indexDB: false },
        });
    }

    /**
     * This endpoint will replace the current handleDeleteUsers endpoint once the Replicant CS Tool is updated to use this endpoint.
     */
    private async handleDeleteUserData(opts: SB.ExtractType<(typeof adminAPISchema)['deleteUserData']>) {
        const results: { [userId: string]: { success: boolean; reason?: string } } = {};
        for (const userId of opts.userIds) {
            try {
                await this.opts.deleteUserData({
                    userId,
                    sources: opts.sources,
                });
                results[userId] = { success: true };
            } catch (e) {
                let errorMessage;
                if (e instanceof Error) {
                    errorMessage = e.message;
                } else {
                    errorMessage = 'Unknown error';

                    try {
                        logger.warn(JSON.stringify(e));
                    } catch (jsonErr) {
                        logger.warn('Could not parse unknown admin delete request error.');
                    }
                }
                results[userId] = { success: false, reason: errorMessage };
            }
        }
        return results;
    }

    private async handleGetScheduledLineChatbotBroadcasts(): Promise<{ id: string; opts: unknown; sendAt: number }[]> {
        // Read scheduled broadcasts from the global system scheduled action user's metainfo:
        const globalMetainfoMVCC = await this.opts.db.metainfoMVCC.fetchMetainfoMVCC(
            GLOBAL_SYSTEM_SCHEDULED_ACTION_USER_ID,
        );

        const notificationKeys = Object.values(globalMetainfoMVCC.systemScheduledActionNotifications ?? []);
        const scheduledActions = await Promise.all(
            notificationKeys.map((key) => this.opts.db.scheduledActions.fetch(key)),
        );

        const broadcastScheduledActions = scheduledActions.filter(
            (action): action is ScheduledAction =>
                action?.actionName === SYSTEM_SCHEDULED_ACTION_NAMES.lineChatbotBroadcast,
        );

        const scheduledBroadcasts = broadcastScheduledActions.map((action) => ({
            id: action.notificationId.substring(LINE_CHATBOT_BROADCAST_NOTIFICATION_ID_PREFIX.length),
            opts: (action.actionArgs as SB.ExtractType<SystemScheduledActions['lineChatbotBroadcast']['schema']>)
                .broadcastOpts,
            sendAt: action.whenToExecute,
        }));

        // Read past broadcasts and their statuses:
        const pastBroadcasts = await this.opts.db.lineChatbotBroadcasts.getBroadcasts();

        const pastBroadcastsWithStatus = await Promise.all(
            pastBroadcasts.map(async (broadcast) => {
                const res = {
                    error: broadcast.error,
                    id: broadcast.broadcastId,
                    opts: broadcast.broadcastOpts,
                    sendAt: broadcast.createdAt,
                    status: undefined as unknown,
                };

                if (broadcast.requestId) {
                    res.status = await this.opts.narrowcastAPIClient.getNarrowcastMessageStatus(broadcast.requestId);
                }

                return res;
            }),
        );

        return sortBy([...scheduledBroadcasts, ...pastBroadcastsWithStatus], (broadcast) => broadcast.sendAt);
    }

    private async handleScheduleLineChatbotBroadcasts(
        broadcasts: { id: string; opts: { [key: string]: unknown }; sendAt: number }[],
    ): Promise<void> {
        const now = this.opts.apiAccess.dateNow();

        // Validate broadcasts before scheduling:
        await Promise.all(
            broadcasts.map(async (broadcast) => {
                const delayInMS = broadcast.sendAt - now;
                if (delayInMS < 0) {
                    throw Error(`Invalid sendAt value in ${JSON.stringify(broadcast)}: cannot schedule into the past`);
                }

                const validationResult = await this.opts.narrowcastAPIClient.validateNarrowcastMessages(broadcast.opts);
                if (!validationResult.isValid) {
                    throw new APIError(JSON.stringify(validationResult.error), 400);
                }
            }),
        );

        const scheduledNotificationIds: string[] = [];

        try {
            for (const broadcast of broadcasts) {
                const delayInMS = broadcast.sendAt - now;
                const notificationId = LINE_CHATBOT_BROADCAST_NOTIFICATION_ID_PREFIX + broadcast.id;

                await this.opts.apiAccess.systemScheduledActions.scheduleGlobal.lineChatbotBroadcast({
                    args: { broadcastId: broadcast.id, broadcastOpts: broadcast.opts },
                    delayInMS,
                    notificationId,
                });

                scheduledNotificationIds.push(notificationId);
            }
        } catch (error) {
            // Roll back the already scheduled actions to avoid a partial broadcast:
            for (const notificationId of scheduledNotificationIds) {
                try {
                    await this.opts.apiAccess.systemScheduledActions.unscheduleGlobal(notificationId);
                } catch {
                    // Ignore errors on rollback
                }
            }

            throw error;
        }
    }

    private async handleUnscheduleLineChatbotBroadcasts(broadcastIds: string[]): Promise<void> {
        for (const broadcastId of broadcastIds) {
            const notificationid = LINE_CHATBOT_BROADCAST_NOTIFICATION_ID_PREFIX + broadcastId;

            await this.opts.apiAccess.systemScheduledActions.unscheduleGlobal(notificationid);
        }
    }

    private async handleFetchSharedState(opts: {
        stateId: string;
        stateName: string;
    }): Promise<{ counters: SharedStateCounterMap; global: unknown; users: { [userId: string]: unknown } }> {
        const { stateId, stateName } = opts;

        const { sharedState } = await this.opts.apiAccess.sharedStates.fetchSharedState({ stateId, stateName });

        if (!sharedState) {
            throw new APIError('Shared state not found', 404);
        }

        return {
            counters: sharedState.counters,
            global: sharedState.global,
            users: sharedState.users,
        };
    }

    private async handleFetchSharedStateIndexSchemas(): Promise<{ [stateName: string]: JSONSchema }> {
        const indexSchemas: { [stateName: string]: JSONSchema } = {};

        const sharedStates: SharedStates = this.opts.apiAccess.config.sharedStates ?? {};

        for (const stateName in sharedStates) {
            const sharedState = sharedStates[stateName]!;

            if (sharedStateHasSearchableProperties(sharedState)) {
                const indexSchema = computedPropertiesToQuerySchema(sharedState.computedProperties);

                indexSchemas[stateName] = indexSchema;
            }
        }

        return indexSchemas;
    }

    private async handleFetchSharedStateMessages(): Promise<{ [stateName: string]: JSONSchema }> {
        const messageArgSchemas: { [stateName: string]: JSONSchema } = {};

        const sharedStates: SharedStates = this.opts.apiAccess.config.sharedStates ?? {};

        for (const stateName in sharedStates) {
            const sharedStateMsgs = sharedStates[stateName]?.messages ?? {};

            for (const messageName in sharedStateMsgs) {
                const { schema } = sharedStateMsgs[messageName]!;

                messageArgSchemas[messageName] = schemaBuilderToJSONSchema(schema);
            }
        }

        return messageArgSchemas;
    }

    private async handlePostSharedStateMessage(opts: {
        message: { name: string; args: unknown };
        stateName: string;
        stateIds: string[];
    }): Promise<{ failedUserIds: string[] }> {
        const { stateIds, stateName } = opts;

        const sharedState = this.opts.apiAccess.config.sharedStates?.[stateName];

        if (!sharedState) {
            throw new APIError(`Shared state ${stateName} not found in Replicant configuration`, 404);
        }

        const message = sharedState.messages?.[opts.message.name];

        if (!message) {
            throw new APIError(`Message ${opts.message.name} not found in shared state configuration`, 404);
        }

        const validationError = message.schema.validate(opts.message.args);

        if (validationError) {
            throw new APIError(`Invalid message arguments: ${validationError}`, 400);
        }

        const failedUserIds: string[] = [];
        const now = this.opts.apiAccess.dateNow();

        // Post messages in batches of 100:
        const stateIdChunks = chunk(stateIds, 100);

        for (const chunkIds of stateIdChunks) {
            const delayedActions: SharedStateDelayedAction[] = chunkIds.map((stateId) => ({
                type: 'postSharedStateMessage',
                async: false, // unused
                message: {
                    id: generateMessageId(now, 0),
                    args: opts.message.args,
                    sender: ADMIN_MESSAGE_SENDER_ID,
                    name: opts.message.name,
                    senderClockOffset: 0,
                },
                stateId,
                stateName,
            }));

            try {
                await this.opts.apiAccess.resolveDelayedActions(delayedActions);
            } catch (error: any) {
                await this.opts.apiAccess.reportError(error, { stateName, stateIds: chunkIds, message: opts.message });

                failedUserIds.push(...chunkIds);
            }
        }

        return { failedUserIds };
    }

    private async handleSearchSharedStates(opts: {
        stateName: string;
        where: unknown;
        limit?: number;
    }): Promise<{ results: any[] }> {
        return this.opts.apiAccess.searchSharedStates(opts.stateName, {
            limit: opts.limit ?? 100,
            where: opts.where as QueryFilters<any>,
        });
    }

    private async handleCountSharedStates(opts: { stateName: string; where: unknown }): Promise<number> {
        return this.opts.apiAccess.countSharedStates(opts.stateName, {
            where: opts.where as QueryFilters<any>,
        });
    }

    private handleFetchKVStore(opts: { keys: string[]; internal: boolean }): Promise<KeyValuePairs> {
        const kvStore = opts.internal ? this.opts.apiAccess.internalKvStore : this.opts.apiAccess.kvStore;
        return kvStore.getBatch(opts.keys);
    }

    private async handleUpdateKVStore(opts: { pairs: KeyValuePairs; internal: boolean }): Promise<void> {
        const kvStore = opts.internal ? this.opts.apiAccess.internalKvStore : this.opts.apiAccess.kvStore;
        await kvStore.sendBatch(opts.pairs);
    }
}
